import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";

import axios from "../axiosConfig";
import { BASE_SERVER_URL } from "../sampleData";
import "../run-sheet-builder.css";

/* ------------------------------------------------------------------ *
 * Run sheet builder — /run-sheet/new  and  /run-sheet/:id/edit
 *
 * Its own route, popped out in its own window by the work order list's
 * "Make run sheet" button (matching the old window.open("/RunSheetMaking")
 * popup). Replaces RunSheetMakingPage in components/movableDragDropArea.js.
 *
 * Create: the list keeps the selection in localStorage under
 * "rf-run-draft" and rewrites it on every pick; this page follows along
 * through the `storage` event:
 *   { date: "DD/MM/YYYY", deliveries: [ …delivery elements, products
 *     still as arrays… ] }  — the same element shape POST /run/AddNew
 * wants. Edit: the run is fetched by id from GET /run/getRun/:id, and
 * when the list is editing it (Delivery runs → Edit opens both) the
 * draft carries `runId` — this page then follows that draft the same way,
 * starting from the saved stops so their order and times survive. A draft
 * for any other run (or a new one) is ignored.
 * ------------------------------------------------------------------ */

const DATE_FMT = "DD/MM/YYYY";
const DRAFT_KEY = "rf-run-draft";

const safeParse = (raw, fallback) => {
  if (raw === undefined || raw === null || raw === "") return fallback;
  if (typeof raw !== "string") return raw;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
};

const locKey = (location) => {
  const loc = safeParse(location, {}) || {};
  const c = loc.coordinates || {};
  return c.lat && c.lng
    ? `${c.lat}_${c.lng}`
    : String(loc.name || "").toLowerCase().replace(/\s+/g, "");
};

const isCasket = (el) => el.product_type === "Casket Cover";

const TAG = {
  "Casket Cover": { label: "Casket", cls: "casket", color: "var(--rf-brand)" },
  Tributes: { label: "Tribute", cls: "tribute", color: "var(--rf-brand-lite)" },
  "Flower Arrangements": { label: "Retail", cls: "tribute", color: "var(--rf-accent)" },
};

/* "2 x CC01" for casket covers, "3 x Wreath" aggregates for the rest —
   the same strings legacy shorten_product_list_2 produced for the
   `shortened` field the run views render. */
function shortenProducts(products) {
  const out = [];
  const agg = {};
  products.forEach((p) => {
    const n = Number(p.amount) || 1;
    if (p.product_type === "Casket cover" && p.code) {
      out.push(`${n} x ${p.code}`);
    } else {
      const key = p.product_type || p.product_name || "Product";
      agg[key] = (agg[key] || 0) + n;
    }
  });
  Object.keys(agg).forEach((k) => out.push(`${agg[k]} x ${k}`));
  return out;
}

/* Group flat delivery elements into stops — one stop per destination,
   keyed the way legacy MergeWorkOrders keyed (coordinates, else the
   normalised name). Order matters: the sequence IS the route. */
/* The service time of the funeral a stop serves — any funeral delivery
   at the stop carries it; retail stops have none. */
const serviceTimeOf = (stop) =>
  (stop.elements.find((el) => el.order_type === "Funeral" && el.funeral_time) || {})
    .funeral_time || "";

function buildStops(deliveries) {
  const byDest = new Map();
  deliveries.forEach((el, i) => {
    const key = locKey(el.location) || `stop-${i}`;
    if (!byDest.has(key)) {
      byDest.set(key, {
        key,
        dest: (safeParse(el.location, {}) || {}).name || "Unknown destination",
        time: el.time || "",
        elements: [],
      });
    }
    byDest.get(key).elements.push(el);
  });
  return Array.from(byDest.values());
}

/* Fold a fresh draft into the stops on screen: stops already here keep
   their place in the route and any time typed for them, new destinations
   join the end, unpicked ones drop out. `removed` holds stops taken off
   in this window, until the list unpicks them too. */
function mergeStops(prev, deliveries, removed) {
  const fresh = buildStops(deliveries);
  const freshKeys = new Set(fresh.map((s) => s.key));
  removed.forEach((k) => {
    if (!freshKeys.has(k)) removed.delete(k);
  });
  const byKey = new Map(fresh.map((s) => [s.key, s]));
  const keep = (s, old) =>
    old
      ? { ...s, time: old.time, elements: s.elements.map((el) => ({ ...el, time: old.time })) }
      : s;
  const out = [];
  prev.forEach((old) => {
    if (byKey.has(old.key)) {
      out.push(keep(byKey.get(old.key), old));
      byKey.delete(old.key);
    }
  });
  byKey.forEach((s) => {
    if (!removed.has(s.key)) out.push(s);
  });
  return out;
}

export function RunSheetBuilderPage({ editRun }) {
  const navigate = useNavigate();
  const { id } = useParams();

  const [date, setDate] = useState("");
  const [driver, setDriver] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [depart, setDepart] = useState("");
  const [description, setDescription] = useState("");
  const [stops, setStops] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingRun, setLoadingRun] = useState(Boolean(editRun));
  const [otherRuns, setOtherRuns] = useState([]);
  const removedRef = useRef(new Set());

  /* ---------- seed: draft (create) or fetch (edit) ---------- */
  useEffect(() => {
    if (editRun && id) {
      let loaded = false;
      /* only a draft the list is writing for THIS run, and only once the
         saved stops are on screen to merge into */
      const readEditDraft = () => {
        if (!loaded) return;
        const draft = safeParse(localStorage.getItem(DRAFT_KEY), {}) || {};
        if (draft.runId !== id) return;
        setStops((prev) =>
          mergeStops(prev, draft.deliveries || [], removedRef.current)
        );
      };
      axios
        .get(BASE_SERVER_URL + "/run/getRun/" + id)
        .then((doc) => {
          setLoadingRun(false);
          if (doc.data.status !== "success" || !doc.data.response[0]) return;
          const run = doc.data.response[0];
          setDate(run.delivery_date || "");
          setDriver(run.drivers_name || "");
          setVehicle(run.vehicle_number || "");
          setDepart(run.comment || "");
          setDescription(run.description || "");
          setStops(
            buildStops(
              (run.deliveries || []).map((el) => ({
                ...el,
                products: safeParse(el.products, []),
              }))
            )
          );
          loaded = true;
          readEditDraft();
        })
        .catch(() => setLoadingRun(false));
      const onStorage = (e) => {
        if (e.key === DRAFT_KEY && e.newValue) readEditDraft();
      };
      window.addEventListener("storage", onStorage);
      return () => window.removeEventListener("storage", onStorage);
    } else {
      const readDraft = () => {
        const draft = safeParse(localStorage.getItem(DRAFT_KEY), {}) || {};
        setDate(draft.date || moment().format(DATE_FMT));
        setStops((prev) => mergeStops(prev, draft.deliveries || [], removedRef.current));
      };
      readDraft();
      /* the list rewrites the draft on every pick */
      const onStorage = (e) => {
        if (e.key === DRAFT_KEY && e.newValue) readDraft();
      };
      window.addEventListener("storage", onStorage);
      return () => window.removeEventListener("storage", onStorage);
    }
  }, [editRun, id]);

  /* Other runs on the same day — run numbering + the double-booking check. */
  useEffect(() => {
    if (!date) return;
    axios
      .post(BASE_SERVER_URL + "/run/getAllAddedProducts/", {
        date,
        ...(editRun && id ? { exclude: id } : {}),
      })
      .then((doc) => setOtherRuns(doc.data.response || []))
      .catch(() => {});
  }, [date, editRun, id]);

  const allElements = useMemo(() => stops.flatMap((s) => s.elements), [stops]);
  const productCount = useMemo(
    () => allElements.reduce((n, el) => n + (el.products || []).length, 0),
    [allElements]
  );
  /* Singles, petals and the rest travel in the casket-cover delivery but
     aren't casket covers — only real ones count. */
  const casketCount = useMemo(
    () =>
      allElements
        .filter(isCasket)
        .reduce(
          (n, el) =>
            n +
            (el.products || []).filter(
              (p) => String(p.product_type || "").toLowerCase() === "casket cover"
            ).length,
          0
        ),
    [allElements]
  );
  const orderCount = useMemo(
    () => new Set(allElements.map((el) => el.work_order_id)).size,
    [allElements]
  );

  const lastStop = stops.length ? stops[stops.length - 1].time || "—" : "—";
  const dayLabel = date ? moment(date, DATE_FMT).format("ddd D MMM") : "";
  const runNumber = otherRuns.length + 1;

  /* Reordering is the point of this screen — the sequence is the route. */
  function onDrop(target) {
    if (dragIndex === null || dragIndex === target) return;
    setStops((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(target, 0, moved);
      return next;
    });
    setDragIndex(null);
  }

  function setStopTime(index, value) {
    setStops((prev) =>
      prev.map((s, i) =>
        i === index
          ? { ...s, time: value, elements: s.elements.map((el) => ({ ...el, time: value })) }
          : s
      )
    );
  }

  function removeStop(index) {
    if (stops[index]) removedRef.current.add(stops[index].key);
    setStops((prev) => prev.filter((_, i) => i !== index));
  }

  const doubleBooked = useMemo(
    () =>
      Boolean(driver) &&
      otherRuns.some(
        (r) =>
          ((r.runDetails || {}).drivers_name || "").trim().toLowerCase() ===
          driver.trim().toLowerCase()
      ),
    [driver, otherRuns]
  );

  const checks = [
    casketCount > 0
      ? {
          text: `All ${casketCount} casket covers must arrive 90 minutes before their service.`,
          tone: "warn",
        }
      : null,
    doubleBooked
      ? {
          text: `${driver} is already on another run today — check the times don't overlap.`,
          tone: "danger",
        }
      : null,
    {
      text: "Saving locks these products to this run and removes them from the pool.",
      tone: "info",
    },
  ].filter(Boolean);

  function save() {
    if (!stops.length || saving) return;
    /* deliveries go up in stop order — one element per work order per
       product group, products/shortened as JSON strings, exactly what
       the legacy builder posted. */
    const deliveries = stops.flatMap((s) =>
      s.elements.map((el) => ({
        product_type: el.product_type,
        work_order_id: el.work_order_id,
        parent_funeral_id: el.parent_funeral_id,
        name: el.name,
        ...(el.order_type === "Funeral"
          ? {
              fd_code: el.fd_code,
              late_person_name: el.late_person_name,
              funeral_time: el.funeral_time || "",
            }
          : {}),
        order_type: el.order_type,
        time: el.time || s.time,
        location:
          typeof el.location === "string" ? el.location : JSON.stringify(el.location),
        shortened: JSON.stringify(shortenProducts(el.products || [])),
        products: JSON.stringify(el.products || []),
      }))
    );
    const payload = {
      drivers_name: driver,
      vehicle_number: vehicle,
      delivery_date: date,
      description,
      comment: depart,
      cross_checked: "",
      deliveries,
    };
    setSaving(true);
    const req =
      editRun && id
        ? axios.post(BASE_SERVER_URL + "/run/editRun", {
            run_id: id,
            update_data: payload,
          })
        : axios.post(BASE_SERVER_URL + "/run/AddNew", payload);
    req
      .then((doc) => {
        setSaving(false);
        if (doc.data.status === "success") {
          toast.success(editRun ? "Run sheet updated" : "Run sheet saved");
          const draft = safeParse(localStorage.getItem(DRAFT_KEY), {}) || {};
          if (!editRun || draft.runId === id) localStorage.removeItem(DRAFT_KEY);
          /* tell the work-order list tab (if open) to leave selection
             mode and refresh its committed-products map */
          localStorage.setItem("rf-run-saved", String(Date.now()));
          /* popped out (by the list, or by Delivery runs → Edit) → hand
             control back to the tab that opened it */
          if (window.opener && !window.opener.closed) {
            window.close();
            return;
          }
          navigate("/DeliveryRunList");
        } else {
          toast.error("The server refused the run sheet.");
        }
      })
      .catch(() => {
        setSaving(false);
        toast.error("Couldn't save the run sheet.");
      });
  }

  return (
    <div className="rs-page">
      <header className="rs-head">
        <div className="rs-head-main">
          <div className="rf-label">
            Run sheet · {editRun ? "editing" : "unsaved"}
          </div>
          <h1 className="rf-serif rs-title">
            {dayLabel}
            {editRun ? (driver ? ` · ${driver}` : "") : ` · Run ${runNumber}`}
          </h1>
          <div className="rs-sub">
            {stops.length} stops · {productCount} products · drag a stop to
            reorder the route
          </div>
        </div>
        <span className="rf-spacer" />
        <div className="rs-head-actions">
          <button
            type="button"
            className="rf-btn rf-btn-ghost"
            onClick={() =>
              editRun && id
                ? window.open(`/run-sheet/print/${id}`, "_blank")
                : window.print()
            }
          >
            Print
          </button>
          <button
            type="button"
            className="rf-btn rf-btn-primary"
            disabled={!stops.length || saving}
            onClick={save}
          >
            {saving ? "Saving…" : "Save run sheet"}
          </button>
        </div>
      </header>

      <div className="rs-body">
        {/* ---------- route ---------- */}
        <div className="rs-route">
          <div className="rs-route-head">
            <span className="rf-label">Route</span>
            <span className="rs-rule" />
            <span className="rs-window">
              {depart ? `${depart} depart · ` : ""}
              {lastStop} last stop
            </span>
          </div>

          {loadingRun && <div className="rs-empty">Loading run sheet…</div>}
          {!loadingRun && stops.length === 0 && (
            <div className="rs-empty">
              Nothing selected yet. Pick products on the work order list —
              they appear here as you go.
            </div>
          )}

          {stops.map((stop, i) => (
            <div
              key={stop.key}
              className={`rs-stop${dragIndex === i ? " is-dragging" : ""}`}
              draggable
              onDragStart={() => setDragIndex(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(i)}
              onDragEnd={() => setDragIndex(null)}
            >
              <div className="rs-stop-head">
                <span className="rs-grip" aria-hidden="true">
                  ⠿
                </span>
                <span className="rs-stop-n rf-num">{i + 1}</span>
                <div className="rs-stop-text">
                  <div className="rs-stop-dest">
                    {stop.dest}
                    {serviceTimeOf(stop) && (
                      <span className="rs-stop-service">
                        {" "}· Funeral service time{" "}
                        <b className="rf-num">{serviceTimeOf(stop)}</b>
                      </span>
                    )}
                  </div>
                  <div className="rs-stop-address">
                    {[...new Set(stop.elements.map((el) => el.name))].join(" · ")}
                  </div>
                </div>
                <label className="rs-arrive">
                  <span>arrive</span>
                  <input
                    className="rf-num"
                    value={stop.time}
                    onChange={(e) => setStopTime(i, e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="rs-remove"
                  onClick={() => removeStop(i)}
                  aria-label={`Remove ${stop.dest}`}
                >
                  ×
                </button>
              </div>

              {stop.elements.flatMap((el, ei) =>
                (el.products || []).map((p, pi) => {
                  const tag = TAG[el.product_type] || TAG.Tributes;
                  return (
                    <div key={`${ei}-${pi}`} className="rs-line">
                      <span
                        className="rs-line-accent"
                        style={{ background: tag.color }}
                      />
                      <div className="rs-line-text">
                        <div className="rs-line-name">
                          {p.code || p.product_name || p.name || p.product_type}
                        </div>
                        <div className="rs-line-order">{el.name}</div>
                      </div>
                      <span className="rs-line-qty rf-num">
                        ×{Number(p.amount) || 1}
                      </span>
                      <span className={`rs-line-tag is-${tag.cls}`}>{tag.label}</span>
                    </div>
                  );
                })
              )}
            </div>
          ))}
        </div>

        {/* ---------- rail ---------- */}
        <aside className="rs-rail">
          <div className="rs-card">
            <div className="rs-card-title">Run details</div>

            <label className="rs-field">
              <span className="rf-label">Driver</span>
              <input
                value={driver}
                placeholder="Driver's name"
                onChange={(e) => setDriver(e.target.value)}
              />
            </label>

            <div className="rs-field-row">
              <label className="rs-field">
                <span className="rf-label">Vehicle</span>
                <input
                  className="rf-num"
                  value={vehicle}
                  placeholder="e.g. VN-014"
                  onChange={(e) => setVehicle(e.target.value)}
                />
              </label>
              <label className="rs-field">
                <span className="rf-label">Depart</span>
                <input
                  className="rf-num"
                  value={depart}
                  placeholder="07:15"
                  onChange={(e) => setDepart(e.target.value)}
                />
              </label>
            </div>

            <label className="rs-field">
              <span className="rf-label">Description</span>
              <input
                value={description}
                placeholder="e.g. Northern suburbs morning run"
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>
          </div>

          <div className="rs-summary">
            <div className="rs-summary-label">Load summary</div>
            {[
              { k: "Stops", v: stops.length },
              { k: "Products", v: productCount },
              { k: "Casket covers", v: casketCount },
              { k: "Orders covered", v: orderCount },
            ].map((t) => (
              <div key={t.k} className="rs-summary-row">
                <span>{t.k}</span>
                <span className="rf-num">{t.v}</span>
              </div>
            ))}
          </div>

          <div className="rs-card">
            <div className="rs-card-title">Before you save</div>
            {checks.map((c) => (
              <div key={c.text} className="rs-check">
                <span className={`rs-check-dot is-${c.tone}`} />
                <span>{c.text}</span>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}

export default RunSheetBuilderPage;
